import { LitElement, html, css } from 'lit';
import { designSystem } from './styles/design-system.js';
import { authService } from './services/auth-service.js';
import { liveCallsService } from './services/live-calls-service.js';

// Import login page (always needed)
import './components/login-page.js';

// Import dashboard components (lazy — only when authenticated)
import './components/app-shell.js';
import './components/dashboard/dashboard-page.js';
import './components/live-calls/live-calls-page.js';
import './components/calls/calls-page.js';
import './components/analytics/analytics-page.js';
import './components/agents/agent-page.js';
import './components/settings/settings-page.js';
import './components/phone/phone-page.js';
import './components/knowledge/knowledge-page.js';
import './components/ivr/ivr-page.js';
import './components/campaigns/campaigns-page.js';
import './components/contacts/contacts-page.js';
import './components/guests/guest-page.js';

/**
 * Voice AI Dashboard — Main Application
 * Routes between login and dashboard based on auth state
 */
export class VoiceDashboard extends LitElement {
  static properties = {
    currentPage: { type: String },
    connected: { type: Boolean },
    stats: { type: Object },
    activeCalls: { type: Array },
    _authenticated: { state: true },
    _authChecked: { state: true },
    _user: { state: true }
  };

  static styles = [
    designSystem,
    css`
      :host {
        display: block;
        width: 100%;
        min-height: 100vh;
      }

      :host([authenticated]) {
        min-height: auto;
        height: calc(100vh - 32px);
        background-color: #FFFFFF;
        border-radius: 24px;
        box-shadow:
          0 0 0 1px rgba(28, 25, 23, 0.06),
          0 24px 64px -12px rgba(28, 25, 23, 0.15),
          0 8px 20px -4px rgba(28, 25, 23, 0.08);
        overflow: hidden;
        position: relative;
        z-index: 1;
      }
    `
  ];

  constructor() {
    super();
    this.currentPage = 'dashboard';
    this.connected = false;
    this.stats = {
      activeCalls: 0,
      totalToday: 0,
      avgDuration: 0,
      successRate: 0
    };
    this.activeCalls = [];
    this._liveCallsUnsub = null;
    this._authenticated = false;
    this._authChecked = false;
    this._user = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this._checkAuth();

    // Listen for auth changes
    this._authUnsub = authService.onChange(({ authenticated, user }) => {
      this._authenticated = authenticated;
      this._user = user;
      if (authenticated) {
        this.setAttribute('authenticated', '');
        this.initializeEventStream();
        this.loadInitialData();
      } else {
        this.removeAttribute('authenticated');
        this.closeEventStream();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.closeEventStream();
    if (this._authUnsub) this._authUnsub();
  }

  async _checkAuth() {
    if (authService.isAuthenticated()) {
      // Validate token with server
      const result = await authService.validateToken();
      this._authenticated = result.valid;
      this._user = result.user || authService.getUser();
      if (result.valid) {
        this.setAttribute('authenticated', '');
        this.initializeEventStream();
        this.loadInitialData();
      }
    }
    this._authChecked = true;
  }

  _handleLoginSuccess(e) {
    this._authenticated = true;
    this._user = e.detail.user;
    this.setAttribute('authenticated', '');
    this.initializeEventStream();
    this.loadInitialData();
  }

  initializeEventStream() {
    try {
      liveCallsService.connect();
      this._liveCallsUnsub = liveCallsService.onChange((calls, connected) => {
        this.activeCalls = calls;
        this.connected = connected;
      });

      console.log('Live calls polling initialized');
    } catch (error) {
      console.error('Failed to initialize live calls:', error);
      this.connected = false;
    }
  }

  closeEventStream() {
    if (this._liveCallsUnsub) {
      this._liveCallsUnsub();
      this._liveCallsUnsub = null;
    }
    liveCallsService.disconnect();
    this.connected = false;
  }

  async loadInitialData() {
    try {
      // Stats are placeholder until dashboard API is built
      this.stats = {
        activeCalls: 0,
        totalToday: 0,
        avgDuration: 0,
        successRate: 0
      };
      // activeCalls populated by liveCallsService via SSE
    } catch (error) {
      console.error('Failed to load initial data:', error);
    }
  }

  handleNavigation(e) {
    this.currentPage = e.detail.page;
  }

  renderPage() {
    switch (this.currentPage) {
      case 'dashboard':
        return html`<modern-dashboard></modern-dashboard>`;
      case 'live-calls':
        return html`<live-calls-monitor .calls="${this.activeCalls}" .connected="${this.connected}"></live-calls-monitor>`;
      case 'history':
        return html`<calls-page></calls-page>`;
      case 'analytics':
        return html`<analytics-dashboard></analytics-dashboard>`;
      case 'agents':
        return html`<agent-page></agent-page>`;
      case 'settings':
        return html`<settings-page></settings-page>`;
      case 'phone-numbers':
        return html`<phone-page></phone-page>`;
      case 'knowledge-base':
        return html`<knowledge-base></knowledge-base>`;
      case 'ivr':
        return html`<ivr-page></ivr-page>`;
      case 'campaigns':
        return html`<voice-campaigns></voice-campaigns>`;
      case 'contacts':
        return html`<voice-contacts></voice-contacts>`;
      case 'guests':
        return html`<guest-page></guest-page>`;
      default:
        return html`<modern-dashboard></modern-dashboard>`;
    }
  }

  render() {
    // Still checking auth — show nothing (loading screen still visible)
    if (!this._authChecked) return html``;

    // Not authenticated — show login
    if (!this._authenticated) {
      return html`<login-page @login-success="${this._handleLoginSuccess}"></login-page>`;
    }

    // Authenticated — show dashboard
    return html`
      <app-shell
        .currentPage="${this.currentPage}"
        .connected="${this.connected}"
        .activeCalls="${this.activeCalls.length}"
        .user="${this._user}"
        @navigate="${this.handleNavigation}"
        @logout="${() => authService.logout()}"
      >
        ${this.renderPage()}
      </app-shell>
    `;
  }
}

customElements.define('voice-dashboard', VoiceDashboard);

// Remove loading screen
const hideLoading = () => {
  const el = document.getElementById('appLoading');
  if (el) el.classList.add('hidden');
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', hideLoading);
} else {
  hideLoading();
}

console.log('Voice AI System initialized');
